import { useCallback, useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import type { Story } from './api';
import { StoryFrameImage } from './StoryFrameImage';
import { useStoryPlayer } from './use-story-player';

interface StoryViewerProps {
  stories: Story[];
  initialStorySlug: string;
  onClose: () => void;
  onStoryChange?: ((story: Story) => void) | undefined;
}

/** Below this, a pointer gesture was a tap rather than a hold. */
const HoldThresholdMs = 200;

/** Vertical travel that dismisses. */
const SwipeDownPx = 90;

/** Horizontal travel that counts as a swipe between frames. */
const SwipeSidePx = 48;

/**
 * Full-screen story viewer.
 *
 * Gestures follow Instagram — left third back, the rest forward, press-and-hold
 * pauses, swipe down closes — and every one of them has a keyboard equivalent,
 * because a viewer that only answers to touch is one a large share of the
 * audience cannot use at all.
 */
export function StoryViewer({
  stories,
  initialStorySlug,
  onClose,
  onStoryChange,
}: StoryViewerProps) {
  const {
    state,
    story,
    frame,
    prefersReducedMotion,
    next,
    previous,
    hold,
    release,
    togglePause,
    onFrameReady,
    onFrameFailed,
  } = useStoryPlayer({ stories, initialStorySlug, onExit: onClose, onStoryChange });

  const containerRef = useRef<HTMLDivElement>(null);
  const pointerRef = useRef<{ x: number; y: number; at: number } | null>(null);

  // --- focus, focus trap, scroll lock --------------------------------------
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const container = containerRef.current;

    container?.focus();

    // Locking with `position: fixed` rather than `overflow: hidden`: on iOS the
    // latter does not reliably stop the page behind, and simply restoring
    // overflow afterwards loses the reader's place on the home page.
    const scrollY = window.scrollY;
    const { position, top, width, overflow } = document.body.style;

    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.position = position;
      document.body.style.top = top;
      document.body.style.width = width;
      document.body.style.overflow = overflow;
      window.scrollTo(0, scrollY);

      // Returning focus lets a keyboard user carry on from the tile they opened,
      // instead of being dropped at the top of the document.
      previouslyFocused?.focus?.();
    };
  }, []);

  // --- keyboard -------------------------------------------------------------
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key === 'ArrowRight') {
        next();
        return;
      }

      if (event.key === 'ArrowLeft') {
        previous();
        return;
      }

      if (event.key === 'Tab') {
        // Focus trap. Without it Tab walks into the page behind the overlay,
        // which is still there and still focusable.
        const focusable = containerRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled])',
        );

        if (!focusable || focusable.length === 0) {
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    }

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [next, onClose, previous]);

  // --- pointer --------------------------------------------------------------
  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      // Capturing guarantees the matching pointerup lands here even if the
      // finger leaves the element. Without it a drag off-screen leaves the
      // viewer paused forever, with no way back.
      event.currentTarget.setPointerCapture(event.pointerId);
      pointerRef.current = { x: event.clientX, y: event.clientY, at: performance.now() };
      hold();
    },
    [hold],
  );

  const onPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const start = pointerRef.current;
      pointerRef.current = null;
      release();

      if (!start) {
        return;
      }

      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      const heldFor = performance.now() - start.at;

      if (dy > SwipeDownPx && Math.abs(dy) > Math.abs(dx)) {
        onClose();
        return;
      }

      // A long press was a deliberate pause; releasing it resumes rather than
      // skipping a frame.
      if (heldFor >= HoldThresholdMs) {
        return;
      }

      if (Math.abs(dx) > SwipeSidePx) {
        if (dx > 0) {
          previous();
        } else {
          next();
        }

        return;
      }

      const bounds = event.currentTarget.getBoundingClientRect();

      if (event.clientX - bounds.left < bounds.width / 3) {
        previous();
      } else {
        next();
      }
    },
    [next, onClose, previous, release],
  );

  // --- preload the next frame ----------------------------------------------
  useEffect(() => {
    // Only the immediate next one. Preloading the whole story competes for
    // bandwidth with the frame the user is actually looking at.
    const upcoming =
      story?.frames[state.frameIndex + 1]?.mediaUrl ??
      stories[state.storyIndex + 1]?.frames[0]?.mediaUrl;

    if (!upcoming) {
      return;
    }

    const image = new Image();
    image.src = upcoming;
  }, [state.frameIndex, state.storyIndex, stories, story]);

  if (!story || !frame) {
    return null;
  }

  const isFirstFrame = state.storyIndex === 0 && state.frameIndex === 0;

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-label={`${story.siteName} hikâyesi`}
      tabIndex={-1}
      // dvh because on iOS Safari 100vh is taller than the visible area, which
      // puts the call to action behind the browser chrome. overscroll-none stops
      // a downward swipe triggering Chrome's pull-to-refresh instead of closing.
      className="fixed inset-0 z-50 flex h-[100dvh] w-screen touch-none select-none overscroll-none flex-col bg-black outline-none [-webkit-touch-callout:none]"
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={release}
      onContextMenu={(event) => event.preventDefault()}
    >
      <StoryFrameImage
        story={story}
        frame={frame}
        failed={state.mediaFailed}
        onReady={onFrameReady}
        onFailed={onFrameFailed}
      />

      {/* Scrims. The artwork is uploaded by an editor and its contrast against
          the overlaid text is unknowable, so both ends get a gradient rather
          than hoping. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/85 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-black/90 via-black/60 to-transparent" />

      {/* Progress and header */}
      <header className="relative z-10 flex flex-col gap-3 px-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <div className="flex gap-1" aria-hidden>
          {story.frames.map((f, index) => (
            <span key={f.id} className="h-0.5 flex-1 overflow-hidden rounded-full bg-white/30">
              <span
                className="block h-full rounded-full bg-white"
                style={{
                  width:
                    index < state.frameIndex
                      ? '100%'
                      : index === state.frameIndex
                        ? `${state.progress * 100}%`
                        : '0%',
                }}
              />
            </span>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {story.siteIconUrl ? (
            <img
              src={story.siteIconUrl}
              alt=""
              width={32}
              height={32}
              className="size-8 rounded-full object-cover"
              style={{ boxShadow: `0 0 0 2px ${story.brandColor}` }}
            />
          ) : null}

          <span className="flex-1 truncate text-sm font-semibold text-white">{story.siteName}</span>

          {/* WCAG 2.2.2: content that moves on its own needs a real mechanism to
              stop it — one that is visible and reachable without a pointer. */}
          <button
            type="button"
            onClick={togglePause}
            onPointerDown={(event) => event.stopPropagation()}
            onPointerUp={(event) => event.stopPropagation()}
            aria-label={state.isPausedByUser ? 'Devam et' : 'Duraklat'}
            aria-pressed={state.isPausedByUser}
            className="grid size-11 place-items-center rounded-full text-white/85 transition hover:bg-white/10 hover:text-white"
          >
            {state.isPausedByUser ? (
              <svg aria-hidden viewBox="0 0 24 24" className="size-5 fill-current">
                <path d="M8 5v14l11-7z" />
              </svg>
            ) : (
              <svg aria-hidden viewBox="0 0 24 24" className="size-5 fill-current">
                <path d="M6 4h4v16H6zM14 4h4v16h-4z" />
              </svg>
            )}
          </button>

          <button
            type="button"
            onClick={onClose}
            onPointerDown={(event) => event.stopPropagation()}
            onPointerUp={(event) => event.stopPropagation()}
            aria-label="Hikâyeyi kapat"
            className="grid size-11 place-items-center rounded-full text-white/85 transition hover:bg-white/10 hover:text-white"
          >
            <svg aria-hidden viewBox="0 0 24 24" className="size-5 fill-current">
              <path d="M18.3 5.7 12 12l6.3 6.3-1.4 1.4L10.6 13.4 4.3 19.7 2.9 18.3 9.2 12 2.9 5.7l1.4-1.4L10.6 10.6l6.3-6.3z" />
            </svg>
          </button>
        </div>
      </header>

      {/* Copy and call to action */}
      <div className="relative z-10 mt-auto flex flex-col gap-3 px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        {frame.headline ? (
          <h2 className="text-2xl font-extrabold leading-tight text-white drop-shadow-lg">
            {frame.headline}
          </h2>
        ) : null}

        {frame.subline ? (
          <p className="text-sm text-white/85 drop-shadow">{frame.subline}</p>
        ) : null}

        {/*
          A real anchor, navigating natively. Routing the click through an
          awaited tracking call first would break the user-gesture chain and get
          the tab blocked on iOS Safari — the one action in this whole feature
          that earns anything. Attribution is the server's job, via /go/{slug}.
        */}
        <a
          href={story.clickUrl}
          target="_blank"
          rel="noreferrer noopener sponsored"
          // Inside the tap surface, so its own activation must not also count as
          // "advance a frame".
          onPointerDown={(event) => event.stopPropagation()}
          onPointerUp={(event) => event.stopPropagation()}
          className="gf-button gf-button-primary mt-1 w-fit"
        >
          {frame.callToAction ?? `${story.siteName} ile oyna`}
          <svg aria-hidden viewBox="0 0 24 24" className="size-4 fill-current">
            <path d="M13 5l7 7-7 7v-4H4v-6h9V5z" />
          </svg>
        </a>
      </div>

      {/* Real, focusable controls. Visually hidden because the gestures cover the
          same ground for a pointer user, but they are the keyboard path. */}
      <div className="sr-only">
        <button type="button" onClick={previous} disabled={isFirstFrame}>
          Önceki kare
        </button>
        <button type="button" onClick={next}>
          Sonraki kare
        </button>
      </div>

      {/* Announces frame changes without stealing focus. Keyed to the frame, so
          it fires once per change rather than on every progress tick. */}
      <p className="sr-only" role="status" aria-live="polite">
        {story.siteName}, kare {state.frameIndex + 1} / {state.frameCount}
        {frame.headline ? `. ${frame.headline}` : ''}
      </p>

      {prefersReducedMotion ? (
        <p className="pointer-events-none absolute inset-x-0 bottom-1 z-10 text-center text-xs text-white/50">
          Otomatik geçiş kapalı — ilerlemek için dokunun
        </p>
      ) : null}

      {state.isBuffering ? (
        <span
          aria-hidden
          className="absolute left-1/2 top-1/2 size-8 -translate-x-1/2 -translate-y-1/2 animate-spin rounded-full border-2 border-white/30 border-t-white"
        />
      ) : null}
    </div>
  );
}
